// Gate data hook — resolves the caller's unmet required agreements + the gate
// state machine, mock-vs-real aware.
//
//   - REAL mode  (USE_REAL_API): read `active_membership.required_agreements`
//     off the /auth/me query. /auth/me is terms-exempt so it never 503s; the
//     real-mode transient (503 `terms_verification_unavailable`) is surfaced by
//     the gate itself when a submit/refresh hits it.
//   - MOCK mode: read GET /agreements/required (MSW), which can return 503 to
//     drive the transient-error screen.
//
// Status: 'loading' | 'none' | 'signing' | 'waiting' | 'unavailable'. A
// non-empty required list maps to SIGNING when the current user can accept
// (org owner / authorized), else WAITING.

import { useQuery } from '@tanstack/react-query'
import { USE_REAL_API } from '../../lib/api'
import { fetchMe } from '../../lib/auth'
import {
  AgreementsError,
  fetchRequiredAgreements,
  type RequiredAgreementItem,
} from '../../api/agreements'

type GateStatus = 'loading' | 'none' | 'signing' | 'waiting' | 'unavailable'

export type RequiredAgreementsResult = {
  status: GateStatus
  agreements: RequiredAgreementItem[]
  refetch: () => void
  isFetching: boolean
}

export function useRequiredAgreements(enabled: boolean): RequiredAgreementsResult {
  const isMock = !USE_REAL_API

  const meQ = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    enabled: enabled && !isMock,
    staleTime: 5 * 60 * 1000,
  })

  const reqQ = useQuery({
    queryKey: ['agreements', 'required'],
    queryFn: fetchRequiredAgreements,
    enabled: enabled && isMock,
    retry: false,
    staleTime: 30 * 1000,
  })

  const agreements: RequiredAgreementItem[] = isMock
    ? (reqQ.data ?? [])
    : (meQ.data?.active_membership?.required_agreements ?? [])

  const unavailable =
    isMock && reqQ.isError && reqQ.error instanceof AgreementsError && reqQ.error.isUnavailable

  const activeQ = isMock ? reqQ : meQ

  let status: GateStatus
  if (!enabled) {
    status = 'none'
  } else if (unavailable) {
    status = 'unavailable'
  } else if (activeQ.isLoading) {
    status = 'loading'
  } else if (agreements.length === 0) {
    status = 'none'
  } else {
    status = agreements[0].can_current_user_accept ? 'signing' : 'waiting'
  }

  return {
    status,
    agreements,
    refetch: () => {
      void (isMock ? reqQ.refetch() : meQ.refetch())
    },
    isFetching: activeQ.isFetching,
  }
}
